import { useEffect, useRef } from 'react'
import { InAppReview } from '@capacitor-community/in-app-review'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── localStorage keys ────────────────────────────────────────────────────────
const KEY_FIRST_OPEN                 = 'selah_first_open_date'
const KEY_DEVOTIONS                  = 'selah_devotions_completed_count'
const KEY_DEVOTIONS_DATE             = 'selah_devotions_cached_date'
const KEY_PRAYERS                    = 'selah_prayers_count'
const KEY_PRAYERS_DATE               = 'selah_prayers_cached_date'
const KEY_ANSWERED_PRAYERS           = 'selah_answered_prayers_count'
const KEY_ANSWERED_PRAYERS_DATE      = 'selah_answered_prayers_cached_date'
const KEY_SERMONS                    = 'selah_sermon_notes_count'
const KEY_SERMONS_DATE               = 'selah_sermon_notes_cached_date'
const KEY_GOALS_COMPLETED            = 'selah_goals_completed_count'
const KEY_GOALS_COMPLETED_DATE       = 'selah_goals_completed_cached_date'
const KEY_VERSES                     = 'selah_verses_library_count'
const KEY_VERSES_DATE                = 'selah_verses_library_cached_date'
const KEY_REVIEW_LAST                = 'selah_review_last_shown'
const KEY_DONATION_LAST              = 'selah_donation_last_shown'
const KEY_HAS_DONATED                = 'selah_has_donated'

// ─── Cooldowns ────────────────────────────────────────────────────────────────
const REVIEW_COOLDOWN_MS           = 90  * 24 * 60 * 60 * 1000  // 90 jours
const DONATION_COOLDOWN_MS         = 30  * 24 * 60 * 60 * 1000  // 30 jours (refus)
const DONATION_DONOR_COOLDOWN_MS   = 180 * 24 * 60 * 60 * 1000  // 6 mois (donateur)

// ─── Délai avant l'affichage d'une modal après l'ouverture de l'app ───────────
// « Ne jamais afficher une modal dans les 2 premières secondes après l'ouverture.
//   Attendre que l'utilisateur soit sur l'accueil depuis au moins 3 secondes. »
const HOME_SETTLE_DELAY_MS = 3000

// ─── Helper : vérifie si le cooldown est écoulé (ou si jamais affiché) ────────
function shouldShow(key: string, cooldownMs: number): boolean {
  const stored = localStorage.getItem(key)
  if (!stored) return true
  return Date.now() - new Date(stored).getTime() > cooldownMs
}

// ─── Helper : lire ou fetcher un COUNT avec cache localStorage quotidien ──────
async function getCachedCount(
  keyCount: string,
  keyDate: string,
  fetcher: () => Promise<number>,
): Promise<number> {
  const today = new Date().toISOString().split('T')[0]
  if (localStorage.getItem(keyDate) === today) {
    return parseInt(localStorage.getItem(keyCount) ?? '0', 10) || 0
  }
  const count = await fetcher()
  localStorage.setItem(keyCount, String(count))
  localStorage.setItem(keyDate, today)
  return count
}

// ─── Helper : nombre de jours depuis la première ouverture ────────────────────
function daysSinceFirstOpen(): number {
  const stored = localStorage.getItem(KEY_FIRST_OPEN)
  if (!stored) return 0
  const first = new Date(stored).getTime()
  if (isNaN(first)) return 0
  return Math.floor((Date.now() - first) / (24 * 60 * 60 * 1000))
}

// ─── Standalone: trigger native review dialog (avec cooldown 90 jours) ────────
// ── Appelée aussi depuis les pages prières quand une prière est marquée
//    comme exaucée (déclenchement immédiat, sans délai).
export async function tryRequestReview(): Promise<void> {
  if (!shouldShow(KEY_REVIEW_LAST, REVIEW_COOLDOWN_MS)) return
  localStorage.setItem(KEY_REVIEW_LAST, new Date().toISOString())
  if (!import.meta.env.PROD) console.log('[Engagement] popup notation déclenchée')
  try {
    await InAppReview.requestReview()
  } catch {
    // Web or unsupported platform: silently ignore
  }
}

// ─── Export : marquer l'utilisateur comme donateur ────────────────────────────
// Appelée depuis DonationPopup après un achat IAP réussi ou clic sur lien Stripe.
export function markAsDonated(): void {
  localStorage.setItem(KEY_HAS_DONATED, new Date().toISOString())
  // Toujours mettre à jour KEY_DONATION_LAST pour déclencher aussi le cooldown
  localStorage.setItem(KEY_DONATION_LAST, new Date().toISOString())
  if (!import.meta.env.PROD) console.log('[Engagement] donateur enregistré')
}

// ─── Hook options ─────────────────────────────────────────────────────────────
interface Options {
  streak: number
  profileReady: boolean
  onShowDonation: (days: 7 | 30) => void
}

// ─── useEngagement ────────────────────────────────────────────────────────────
export function useEngagement({ streak, profileReady, onShowDonation }: Options): void {
  const { user } = useAuth()
  const hasRun   = useRef(false)

  const onDonationRef = useRef(onShowDonation)
  onDonationRef.current = onShowDonation

  // Track first open date (once, on mount)
  useEffect(() => {
    if (!localStorage.getItem(KEY_FIRST_OPEN)) {
      localStorage.setItem(KEY_FIRST_OPEN, new Date().toISOString().split('T')[0])
    }
  }, [])

  // Main engagement check (runs once per session, after profile is ready)
  useEffect(() => {
    if (!user || !profileReady || hasRun.current) return
    hasRun.current = true

    const timers: number[] = []

    ;(async () => {
      // ── Compteurs avec cache quotidien ────────────────────────────────────
      const [
        devotionsCount,
        prayersCount,
        answeredPrayersCount,
        sermonNotesCount,
        goalsCompletedCount,
        versesCount,
      ] = await Promise.all([

        getCachedCount(KEY_DEVOTIONS, KEY_DEVOTIONS_DATE, async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count } = await (supabase as any)
            .from('devotional_entries')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
          return count ?? 0
        }),

        getCachedCount(KEY_PRAYERS, KEY_PRAYERS_DATE, async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count } = await (supabase as any)
            .from('prayer_item')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
          return count ?? 0
        }),

        getCachedCount(KEY_ANSWERED_PRAYERS, KEY_ANSWERED_PRAYERS_DATE, async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count } = await (supabase as any)
            .from('prayer_item')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('status', 'answered')
          return count ?? 0
        }),

        getCachedCount(KEY_SERMONS, KEY_SERMONS_DATE, async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count } = await (supabase as any)
            .from('sermon_notes')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
          return count ?? 0
        }),

        getCachedCount(KEY_GOALS_COMPLETED, KEY_GOALS_COMPLETED_DATE, async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count } = await (supabase as any)
            .from('spiritual_goal')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('status', 'accompli')
          return count ?? 0
        }),

        getCachedCount(KEY_VERSES, KEY_VERSES_DATE, async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { count } = await (supabase as any)
            .from('verse_library')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
          return count ?? 0
        }),

      ])

      const ageDays = daysSinceFirstOpen()

      if (!import.meta.env.PROD) {
        console.log('[Engagement] streak:', streak,
          '| dévotions:', devotionsCount,
          '| prières:', prayersCount,
          '| exaucées:', answeredPrayersCount,
          '| sermons:', sermonNotesCount,
          '| objectifs accomplis:', goalsCompletedCount,
          '| versets:', versesCount,
          '| âge compte (j):', ageDays)
      }

      // ── Déclencheurs notation (whichever comes first) ─────────────────────
      // · 2 prières créées
      // · 2 jours de connexion consécutifs
      // · 1 temps dévotionnel complété
      // · 1 prière marquée comme exaucée
      const shouldReview =
        prayersCount         >= 2 ||
        streak               >= 2 ||
        devotionsCount       >= 1 ||
        answeredPrayersCount >= 1

      // ── Déclencheurs donation (whichever comes first) ─────────────────────
      // · 5 jours de connexion consécutifs
      // · 3 prières créées
      // · 2 temps dévotionnels complétés
      // · 1 prière marquée comme exaucée
      // · 1 objectif accompli
      // · 3 versets enregistrés
      // · 1 note de sermon
      // · 10 jours depuis la création du compte
      const shouldDonate =
        streak               >= 5 ||
        prayersCount         >= 3 ||
        devotionsCount       >= 2 ||
        answeredPrayersCount >= 1 ||
        goalsCompletedCount  >= 1 ||
        versesCount          >= 3 ||
        sermonNotesCount     >= 1 ||
        ageDays              >= 10

      // ── Règle de priorité : notation d'abord. Si éligible, on NE montre
      //    PAS la donation cette session. Elle attendra le prochain lancement.
      if (shouldReview && shouldShow(KEY_REVIEW_LAST, REVIEW_COOLDOWN_MS)) {
        const t = window.setTimeout(() => {
          tryRequestReview()
        }, HOME_SETTLE_DELAY_MS)
        timers.push(t)
        return
      }

      // ── Popup donation ────────────────────────────────────────────────────
      if (!shouldDonate) return

      const hasDonated = !!localStorage.getItem(KEY_HAS_DONATED)
      const cooldownMs = hasDonated ? DONATION_DONOR_COOLDOWN_MS : DONATION_COOLDOWN_MS
      if (!shouldShow(KEY_DONATION_LAST, cooldownMs)) return

      const days: 7 | 30 = (streak >= 30 || ageDays >= 30) ? 30 : 7

      const t = window.setTimeout(() => {
        localStorage.setItem(KEY_DONATION_LAST, new Date().toISOString())
        if (!import.meta.env.PROD) console.log('[Engagement] popup donation déclenchée')
        onDonationRef.current(days)
      }, HOME_SETTLE_DELAY_MS)
      timers.push(t)
    })()

    return () => { timers.forEach(t => window.clearTimeout(t)) }
  }, [user, profileReady, streak])

  // ── Trigger notation si prière exaucée (Realtime) ─────────────────────────
  // ── Déclenchement immédiat (l'utilisateur vient juste d'agir) ─────────────
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('answered-prayer-review')
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'prayer_item',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as { status?: string }
          if (updated.status === 'answered') {
            // Invalide le cache pour que la donation se déclenche au prochain lancement
            localStorage.removeItem(KEY_ANSWERED_PRAYERS_DATE)
            tryRequestReview()
          }
        },
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [user])
}
